import {
  Centralizer,
  Flex,
  Form,
  LinkText,
  PasswordField,
  SubmitButton,
  TextInputField,
} from '@/components';
import { VITE_APP_BASENAME } from '@/config';
import { useChangeBrowserTabTitleEffect, useForm } from '@/hooks';
import { setUser } from '@/lib/datadog';
import { useMessage } from '@/providers';
import { isPasswordResettable } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useCallback, useEffect, useRef } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import type { z } from 'zod';

import { apiErrorCodeType } from '@/api/error-codes';
import useUmami from '@/lib/umami';
import { isAxiosError } from 'axios';
import { AuthenticationPagesLayout } from '../_components/AuthenticationPagesLayout';
import { useLogin } from '../_hooks/login';
import { LoginFormSchema } from '../_models/login';

type FormValues = z.infer<typeof LoginFormSchema>;

const LoginPage: FC = () => {
  const { mutateAsync: loginAsync } = useLogin();
  const umami = useUmami();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [, , removeCookie] = useCookies();
  const { openMessage } = useMessage();
  const [searchParams] = useSearchParams();

  // TODO: The previous title may be shown when history back called.
  useChangeBrowserTabTitleEffect({ title: t('signin') });

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(LoginFormSchema),
    reValidateMode: 'onSubmit',
  });

  const onSubmit = useCallback(
    async (values: FormValues) => {
      try {
        const response = await loginAsync(values);
        setUser(response.id);
        umami.identify({
          id: response.id,
          name: response.username,
          email: response.email,
        });
        const redirectUri = searchParams.get('redirectUri');
        navigate(redirectUri ?? '/');
      } catch (error) {
        if (isAxiosError(error)) {
          const errorCode = error.response?.data.error_code;
          if (
            errorCode ===
            apiErrorCodeType.Values
              .BNS_API_COMMON_403_0003_LOGIN_TIME_RESTRICTED
          ) {
            const details = error.response?.data.details;
            try {
              const canLoginFrom = details[0].can_login_from;
              const canLoginTo = details[0].can_login_to;
              await openMessage(
                'warning',
                t('warning-message-login-time-restricted', {
                  canLoginFrom: canLoginFrom,
                  canLoginTo: canLoginTo,
                }),
              );
              return;
            } catch {
              await openMessage(
                'warning',
                t('warning-message-login-time-restricted-fallback'),
              );
              return;
            }
          }
        }

        await openMessage('error', t('error-message-login-failed'));
      }
    },
    [loginAsync, navigate, openMessage, searchParams, t, umami],
  );

  useEffect(() => {
    removeCookie('access_token');
    removeCookie('csrftoken');
  }, [removeCookie]);

  return (
    <>
      <FvMovie />
      <Flex style={{ zIndex: 1000 }}>
        <AuthenticationPagesLayout cardTitle={t('signin')}>
          <Form
            leavePageAlertCondition={null}
            layout="vertical"
            onFinish={handleSubmit(onSubmit)}
            autoComplete="off"
          >
            <TextInputField
              label={t('email')}
              name="email"
              control={control}
              errors={errors}
            />
            <PasswordField
              label={t('password')}
              name="password"
              control={control}
              errors={errors}
            />

            <Form.Item>
              <Centralizer>
                <SubmitButton loading={isSubmitting} text={t('signin')} />
              </Centralizer>
            </Form.Item>
          </Form>

          {isPasswordResettable() && (
            <Centralizer>
              <LinkText
                onClick={() => {
                  navigate('/login/reset-password');
                }}
                text={t('reset-forgot-password')}
              />
            </Centralizer>
          )}
        </AuthenticationPagesLayout>
      </Flex>
    </>
  );
};

export default LoginPage;

const FvMovie: FC = () => {
  const REPEAT_START_SECOND = 12.45;
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) {
      const video = videoRef.current;

      const loop = () => {
        video.currentTime = REPEAT_START_SECOND;
        video.play();
      };

      video.addEventListener('ended', loop);

      return () => {
        video.removeEventListener('ended', loop);
      };
    }
  }, []);

  return (
    <video
      autoPlay
      muted
      playsInline
      ref={videoRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: -1000,
        width: '100%',
        height: '100vh',
        objectFit: 'cover',
      }}
      poster={`${VITE_APP_BASENAME}/ps/img/top_fv_fallback.jpg`}
    >
      <source
        src={`${VITE_APP_BASENAME}/ps/movie/top_fv.mp4`}
        type="video/mp4"
      />
    </video>
  );
};
